import { View, Text, styled, useTheme } from 'tamagui'
import { X, MessageCircle, ShieldBan, Gamepad2, Music, Monitor, Radio, Trophy } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { users, currentUserId } from '@/mocks'
import { Avatar, ProBadge } from '../../common'
import type { UserStatus } from '@/types'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '$overlay',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
})

const Modal = styled(View, {
  width: 340,
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
})

const Banner = styled(View, {
  height: 60,
})

const ProfileBody = styled(View, {
  padding: 16,
  paddingTop: 0,
  gap: 12,
})

const AvatarRow = styled(View, {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginTop: -24,
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const DisplayName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '700',
  fontSize: 20,
})

const UserTag = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 13,
  color: '$color8',
})

const Divider = styled(View, {
  height: 1,
  backgroundColor: '$borderDefault',
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const BioText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$surfaceForeground',
  lineHeight: 18,
})

const StatusBadge = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
})

const StatusDot = styled(View, {
  width: 8,
  height: 8,
  borderRadius: '$borderRadiusSm',
})

const StatusText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  textTransform: 'capitalize',
})

const ActionButtonRow = styled(View, {
  flexDirection: 'row',
  gap: 8,
})

const ActionBtn = styled(View, {
  flex: 1,
  height: 32,
  borderRadius: '$borderRadiusMd',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, opacity 150ms ease' },
  variants: {
    variant: {
      primary: {
        backgroundColor: '$accent',
        hoverStyle: { opacity: 0.9 },
      },
      danger: {
        borderWidth: 1,
        borderColor: '$error',
        hoverStyle: { backgroundColor: 'rgba(207, 62, 79, 0.15)' },
      },
    },
  } as const,
})

const ActionBtnText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
})

const CustomStatusRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  paddingHorizontal: 8,
  paddingVertical: 4,
  backgroundColor: '$sidebarActive',
  borderRadius: '$borderRadiusMd',
})

const CustomStatusText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

const ActivityRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 8,
  paddingHorizontal: 8,
  paddingVertical: 6,
  backgroundColor: '$sidebarActive',
  borderRadius: '$borderRadiusMd',
  borderLeftWidth: 3,
  borderLeftColor: '$accent',
})

const ActivityLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const ActivityName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  fontWeight: '600',
  color: '$surfaceForeground',
})

const ActivityDetails = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

const activityIcons = {
  playing: Gamepad2,
  listening: Music,
  watching: Monitor,
  streaming: Radio,
  competing: Trophy,
}

const activityLabels: Record<string, string> = {
  playing: 'Playing',
  listening: 'Listening to',
  watching: 'Watching',
  streaming: 'Streaming on',
  competing: 'Competing in',
}

const statusColors: Record<UserStatus, string> = {
  online: '#3ECF7B',
  idle: '#E8A840',
  dnd: '#CF3E4F',
  offline: '#505A6E',
}

export function UserProfileModal() {
  const { profileModalUserId, closeProfileModal, navigateToDms, streamerMode } = useAppStore()
  const theme = useTheme()
  const [isBlocked, setIsBlocked] = useState(false)
  const [note, setNote] = useState('')

  if (!profileModalUserId) return null

  const user = users[profileModalUserId]
  if (!user) return null

  const isOwnProfile = profileModalUserId === currentUserId

  return (
    <Overlay onPress={closeProfileModal}>
      <Modal
        onPress={(e: { stopPropagation: () => void }) => e.stopPropagation()}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <Banner backgroundColor={user.roleColor || '$sidebarActive'} />

        <ProfileBody>
          <AvatarRow>
            <View
              borderWidth={4}
              borderColor="$sidebar"
              borderRadius="$borderRadiusFull"
            >
              <Avatar
                name={user.displayName}
                color={user.roleColor || '#2A3142'}
                size="xl"
                status={user.status}
              />
            </View>
            <CloseButton onPress={closeProfileModal}>
              <X size={16} color="$sidebarMuted" />
            </CloseButton>
          </AvatarRow>

          <View>
            <View flexDirection="row" alignItems="center" gap={8}>
              <DisplayName color={user.roleColor || '$surfaceForeground'}>
                {user.displayName}
              </DisplayName>
              {user.isPro && <ProBadge size="sm" />}
            </View>
            <UserTag
              // @ts-expect-error -- web filter
              style={streamerMode ? { filter: 'blur(4px)' } : undefined}
            >
              {user.username}#{user.discriminator}
            </UserTag>
          </View>

          <StatusBadge>
            <StatusDot backgroundColor={statusColors[user.status]} />
            <StatusText>{user.status === 'dnd' ? 'Do Not Disturb' : user.status}</StatusText>
          </StatusBadge>

          {user.activity && (() => {
            const Icon = activityIcons[user.activity.type]
            return (
              <ActivityRow>
                <Icon size={16} color="$accent" style={{ marginTop: 2 }} />
                <View gap={2} flex={1}>
                  <ActivityLabel>{activityLabels[user.activity.type]}</ActivityLabel>
                  <ActivityName>{user.activity.name}</ActivityName>
                  {user.activity.details && (
                    <ActivityDetails>{user.activity.details}</ActivityDetails>
                  )}
                </View>
              </ActivityRow>
            )
          })()}

          {user.customStatus && (
            <CustomStatusRow>
              {user.customStatus.emoji && (
                <Text fontSize={14}>{user.customStatus.emoji}</Text>
              )}
              <CustomStatusText>{user.customStatus.text}</CustomStatusText>
            </CustomStatusRow>
          )}

          {user.bio && (
            <>
              <Divider />
              <View gap={4}>
                <SectionLabel>About Me</SectionLabel>
                <BioText>{user.bio}</BioText>
              </View>
            </>
          )}

          <Divider />
          <View gap={4}>
            <SectionLabel>Note</SectionLabel>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Click to add a note"
              rows={2}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                lineHeight: '18px',
                color: '#C5CBD9',
                backgroundColor: 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 6,
                outline: 'none',
                resize: 'none',
                width: '100%',
                padding: '6px 8px',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#3ECFCF'; e.target.rows = 4 }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; if (!note) e.target.rows = 2 }}
            />
          </View>

          {!isOwnProfile && (
            <>
              <Divider />
              <ActionButtonRow>
                <ActionBtn
                  variant="primary"
                  onPress={() => {
                    navigateToDms()
                    closeProfileModal()
                  }}
                >
                  <MessageCircle size={14} color="$surface" />
                  <ActionBtnText color="$surface">Message</ActionBtnText>
                </ActionBtn>
                <ActionBtn
                  variant="danger"
                  onPress={() => setIsBlocked(!isBlocked)}
                >
                  <ShieldBan size={14} color="$error" />
                  <ActionBtnText color="$error">
                    {isBlocked ? 'Unblock User' : 'Block User'}
                  </ActionBtnText>
                </ActionBtn>
              </ActionButtonRow>
            </>
          )}
        </ProfileBody>
      </Modal>
    </Overlay>
  )
}
